import { currentUrl, historyEvent, loadScript } from '@avensia/excite';
import { styled } from '@glitz/react';
import * as React from 'react';
import {
  PRODUCT_CONFIG_URL_PARAMS,
  FEATURE_VALUE_SEPARATOR,
} from 'webapp/variant-selectors/product-configuration-selectors';
import { createMinLargeViewportDecorator } from 'webapp/viewport';

type CylindoOptions = {
  accountID: string;
  productCode: string;
  features: string[];
  containerID: string;
  progressBar: boolean;
  tooltipDragText: string;
  startFrame: number;
};

type ViewerApi = {
  setFeatures: (features: string[]) => void;
  setProduct: (productCode: string) => void;
};
declare global {
  const cylindo: {
    on: (event: string, callback: () => void) => void;
    viewer: { create: (options: CylindoOptions) => ViewerApi };
  };
}

function loadCss(src: string) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = src;
  document.body.appendChild(link);
}

type PropType = {
  config: NordicNest.Api.ProductConfigurationApiModel;
};

function checkForConcent(): boolean {
  return window?.CookieInformation?.getConsentGivenFor?.('cookie_cat_functional');
}

export default function Cylindo({ config }: PropType) {
  React.useEffect(() => {
    if (!checkForConcent()) {
      return;
    }

    loadCss('//viewer.cylindo.com/v4/viewer.min.css');
    loadScript('//viewer.cylindo.com/v4/viewer.min.js').then(() => {
      const features: string[] = getFeaturues(config.features);
      const options = {
        accountID: config.cylindoAccountId,
        productCode: config.cylindoModel,
        features: features,
        containerID: 'cylindoViewerWrapper',
        tooltipDragText: '',
        progressBar: false,
        startFrame: 4,
      };

      if (cylindo) {
        cylindo.on('ready', function () {
          const viewer = cylindo.viewer.create(options);
          historyEvent.on(ev => {
            if (ev.url.searchParams.has(PRODUCT_CONFIG_URL_PARAMS)) {
              if (features && options.features !== features) {
                viewer.setFeatures(getFeaturues(config.features));
              }
            }
          });
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  if (!checkForConcent()) {
    return <CylindoViewerWrapper />;
  }

  return <CylindoViewerWrapper id="cylindoViewerWrapper" />;
}

const CylindoViewerWrapper = styled.div(
  createMinLargeViewportDecorator({
    height: 640,
  }),
  {
    height: 450,
    zIndex: 0,
  },
);

function getFeaturues(features: NordicNest.Api.FeatureApiModel[]) {
  const array: string[] = [];
  const params = currentUrl().searchParams.getAll(PRODUCT_CONFIG_URL_PARAMS);
  features.forEach(f => {
    array.push(f.cylindoFeature);
    const value = params
      .find(p => p.split(FEATURE_VALUE_SEPARATOR)[0] === f.cylindoFeature)
      ?.split(FEATURE_VALUE_SEPARATOR)[1];
    array.push(value ?? f.options[0].cylindoOption);
  });

  return array;
}
